import React from "react";
import { useSelector } from "react-redux";

import { toDate } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";

import { RightPaneState } from "../../../../app/state/right-pane/RightPaneState";
import { selectTimeRange } from "../../../../app/state/view/timeline/TimelineSelectors";
import type { FullComponent } from "../../../../common/openapi";
import { GenericErrorMessage } from "../../../../components/react/error/GenericErrorMessage";
import { LoadingSpinner } from "../../../../components/react/loading-spinner/LoadingSpinner";
import { type DrawerType, Markdown, type MarkdownProps } from "../../../../components/react/markdown/Markdown";
import { useComponentActions } from "../../../../components/use-component-actions";
import { queryFullComponent } from "../../../../queries";
import { useQuery } from "../../../../queries/react-query";

export interface RemediationGuideProps extends Pick<MarkdownProps, "text"> {
	componentIdentifier: string;
	timestamp: Time;
	showPinnedPane: boolean;
	onInternalLinkClick?: VoidFunction;
}

export const RemediationGuide: React.FC<RemediationGuideProps> = ({
	componentIdentifier,
	timestamp,
	onInternalLinkClick,
	...props
}) => {
	const {
		isLoading,
		error,
		data: component,
	} = useQuery(queryFullComponent({ componentIdOrIdentifier: componentIdentifier, timestamp }));

	if (isLoading) {
		return <LoadingSpinner />;
	}

	if (error || component == null) {
		return <GenericErrorMessage error={error} />;
	}

	return (
		<RemediationMarkdown component={component} linkCallback={onInternalLinkClick} timestamp={timestamp} {...props} />
	);
};

interface RemediationMarkdownProps extends MarkdownProps {
	component: FullComponent;
	timestamp: Time;
	showPinnedPane: boolean;
}

const RemediationMarkdown: React.FC<RemediationMarkdownProps> = ({
	component,
	timestamp,
	showPinnedPane,
	linkCallback,
	deepLinkParams,
	...rest
}) => {
	const timeRange = useSelector(selectTimeRange);
	const actions = useComponentActions(component);

	return (
		<Markdown
			copyCodeToClipboard
			deepLink
			openLinksInNewTab
			{...rest}
			deepLinkParams={{
				...deepLinkParams,
				timestamp: toDate(timestamp),
				...(showPinnedPane && { rightPane: { ...RightPaneState.defaults, hidden: false } }),
			}}
			linkCallback={(drawerType?: DrawerType) => {
				if (drawerType === "logs" && actions.showLogs) {
					actions.showLogs({ timestamp, timeRange });
				} else if (drawerType === "lastChange" && actions.showLastChange) {
					actions.showLastChange();
				} else if (drawerType === "configuration" && actions.showConfiguration) {
					actions.showConfiguration();
				} else if (drawerType === "status" && actions.showStatus) {
					actions.showStatus();
				} else if (drawerType == null) {
					linkCallback?.();
				} else {
					throw new Error("No remediation guide drawer found");
				}
			}}
		/>
	);
};
